import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/useAuth";
import { AgeGate } from "./components/AgeGate";
import { AppShell } from "./components/AppShell";
import { QuickLockProvider } from "./privacy/QuickLockProvider";

const AuthPage = lazy(() => import("./pages/AuthPage").then((module) => ({ default: module.AuthPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const EntryFormPage = lazy(() => import("./pages/EntryFormPage").then((module) => ({ default: module.EntryFormPage })));
const HistoryPage = lazy(() => import("./pages/HistoryPage").then((module) => ({ default: module.HistoryPage })));
const InsightsPage = lazy(() => import("./pages/InsightsPage").then((module) => ({ default: module.InsightsPage })));
const LegalPage = lazy(() => import("./pages/LegalPage").then((module) => ({ default: module.LegalPage })));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage").then((module) => ({ default: module.OnboardingPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const AdminPage = lazy(() => import("./pages/AdminPage").then((module) => ({ default: module.AdminPage })));
const ConnectionsPage = lazy(() => import("./pages/ConnectionsPage").then((module) => ({ default: module.ConnectionsPage })));

const AGE_KEY = "dt:age-verified:v1";

export default function App() {
  const location = useLocation();
  const { user, profile, isAdmin, loading } = useAuth();
  const [ageConfirmed, setAgeConfirmed] = useState(() => localStorage.getItem(AGE_KEY) === "true");
  const legalRoute = ["/privacy", "/terms", "/guidelines"].includes(location.pathname);

  useEffect(() => {
    const discreet = profile?.discreetMode ?? false;
    document.title = discreet ? "Private Log" : "DickerTicker";
  }, [profile?.discreetMode]);

  if (legalRoute) return <PageLoader><Routes><Route path="/privacy" element={<LegalPage kind="privacy" />} /><Route path="/terms" element={<LegalPage kind="terms" />} /><Route path="/guidelines" element={<LegalPage kind="guidelines" />} /></Routes></PageLoader>;

  if (!ageConfirmed) return <AgeGate onConfirm={() => { localStorage.setItem(AGE_KEY, "true"); setAgeConfirmed(true); }} />;
  if (loading) return <main className="splash-screen"><div className="brand__mark">DT</div><p>Opening your private log…</p></main>;
  if (!user) return <PageLoader><AuthPage /></PageLoader>;
  if (!profile) return <PageLoader><OnboardingPage /></PageLoader>;

  return (
    <QuickLockProvider key={user.uid} user={user}><PageLoader><Routes>
      <Route path="/app" element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route path="new" element={<EntryFormPage />} />
        <Route path="edit/:entryId" element={<EntryFormPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="connections" element={<ConnectionsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="admin" element={isAdmin ? <AdminPage /> : <Navigate to="/app" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes></PageLoader></QuickLockProvider>
  );
}

function PageLoader({ children }: { children: ReactNode }) {
  return <Suspense fallback={<main className="splash-screen"><div className="brand__mark">DT</div><p>Opening your private log…</p></main>}>{children}</Suspense>;
}
